/**
 * Global keyboard handler per specs/001-mvp-foundation/contracts/ui-keyboard.md.
 *
 * Focus-aware suppression: when the visitor is typing in the paste box or
 * any input, we only honor `?` and `Escape`. Everywhere else, the full
 * shortcut set is active.
 */

export interface KeyboardHandlers {
  togglePlay: () => void;
  nextItem: () => void;
  prevItem: () => void;
  seekRelative: (seconds: number) => void;
  toggleFullscreen: () => void;
  togglePiP: () => void;
  toggleMute: () => void;
  toggleShortcutsOverlay: () => void;
  closeOverlay: () => void;
}

function isEditable(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return false;
}

export function installKeyboardHandlers(h: KeyboardHandlers): () => void {
  function onKey(e: KeyboardEvent) {
    // Shortcuts overlay toggle and Escape are always honored.
    if (e.key === "?" || (e.shiftKey && e.key === "/")) {
      h.toggleShortcutsOverlay();
      e.preventDefault();
      return;
    }
    if (e.key === "Escape") {
      h.closeOverlay();
      // Don't preventDefault — let fullscreen exit natively if relevant.
      return;
    }

    // Everything else is suppressed when the visitor is typing.
    if (isEditable(e.target)) return;

    // Respect modifier combos — don't steal Ctrl/Cmd+anything.
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    switch (e.key) {
      case " ":
      case "k":
      case "K":
        h.togglePlay();
        e.preventDefault();
        return;
      case "j":
      case "J":
        h.seekRelative(-10);
        e.preventDefault();
        return;
      case "l":
      case "L":
        h.seekRelative(10);
        e.preventDefault();
        return;
      case "ArrowLeft":
        h.prevItem();
        e.preventDefault();
        return;
      case "ArrowRight":
        h.nextItem();
        e.preventDefault();
        return;
      case "f":
      case "F":
        h.toggleFullscreen();
        e.preventDefault();
        return;
      case "p":
      case "P":
        h.togglePiP();
        e.preventDefault();
        return;
      case "m":
      case "M":
        h.toggleMute();
        e.preventDefault();
        return;
      default:
        return;
    }
  }

  document.addEventListener("keydown", onKey);
  return () => document.removeEventListener("keydown", onKey);
}
